import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSelectAll } from '../../utils/useSelectAll';
import { Button } from '../buttons/Button';
import { Checkbox } from '../checkbox/Checkbox';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { Modal } from '../modal/Modal';

import { TableCustomizationItem } from './TableCustomizationItem';
import { TableColumnSettings } from './TableCustomizationModal';

import styles from './table-customization.module.scss';

export type TableCustomFilterModalProps = {
  initialValue: TableColumnSettings[];
  modalOpened: boolean;
  onCommit: (newValue: TableColumnSettings[]) => void;
  onCancel: () => void;
  onReset: () => void;
  size: number;
  withinPortal?: boolean;
};

export function TableCustomFilterModal({
  initialValue,
  modalOpened,
  onCommit,
  onCancel,
  onReset,
  size,
  withinPortal = true,
}: TableCustomFilterModalProps) {
  const [value, setValue] = useState(initialValue);

  const { t } = useTranslation();

  useEffect(() => {
    setValue(initialValue);
  }, [initialValue, modalOpened]);

  const { state: selectAllState, checkboxProps: selectAllCheckboxProps } =
    useSelectAll(
      value,
      () => {
        return value.every(
          (filter) => filter.isVisible || filter.disabled === true
        )
          ? 'all'
          : value.some((filter) => filter.isVisible && filter.disabled !== true)
            ? 'some'
            : 'none';
      },
      (checked) => {
        setValue((value) =>
          value.map((v) => ({
            ...v,
            isVisible: v.disabled ? v.isVisible : checked,
          }))
        );
      }
    );

  return (
    <Modal
      actions={
        <>
          <Button
            icon={<GoogleFontIcon name="close" />}
            onClick={onCancel}
            variant="secondary"
          >
            {t('app.table.filterModal.cancel')}
          </Button>
          <Button
            icon={<GoogleFontIcon name="check" />}
            onClick={() => {
              onCommit(value);
            }}
          >
            {t('app.table.filterModal.apply')}
          </Button>
        </>
      }
      footerAction={
        <div className={styles['footer']}>
          <Checkbox
            className={styles['footer__select-all']}
            label={t('app.table.filterModal.selectAll')}
            data-testid="table-custom-filter-modal__select-all-checkbox"
            {...selectAllCheckboxProps}
          />
          <Button
            className={styles['footer__reset-all']}
            icon={<GoogleFontIcon name="restart_alt" />}
            variant="text"
            onClick={onReset}
            disabled={selectAllState === 'none'}
          >
            {t('app.table.filterModal.resetChanges')}
          </Button>
        </div>
      }
      modalType="action"
      opened={modalOpened}
      onClose={onCancel}
      title={t('app.table.filterModal.title')}
      withinPortal={withinPortal}
      size={size}
    >
      <div className={styles['columns__main']}>
        {value.map((customFilter) => {
          return (
            <TableCustomizationItem
              {...customFilter}
              key={customFilter.id}
              onChange={(id, isVisible) => {
                setValue((value) =>
                  value.map((v) =>
                    id === v.id && !v.disabled ? { ...v, isVisible } : v
                  )
                );
              }}
              showSortingHandle={false}
            />
          );
        })}
      </div>
    </Modal>
  );
}
